import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { useScrollReveal } from "../hooks/use-scroll-reveal";
import { ArrowRight, PaintBucket, Home, Paintbrush, Layers, Fence, CheckCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;

const iconMap = {
  "interior-painting": Paintbrush,
  "exterior-painting": Home,
  "popcorn-ceiling": Layers,
  "garage-floor": PaintBucket,
  "deck-staining": Fence,
};

// Fallback data when API is not available
const fallbackServices = [
  {
    id: "interior-painting",
    title: "Interior Painting",
    description: "Transform your home's interior with professional painting services. We deliver flawless finishes for every room.",
    features: ["Wall & Ceiling Painting", "Trim & Baseboard Painting", "Cabinet Refinishing", "Color Consultation"],
    image: "/images/interior-painting-orlando-florida.jpg",
    alt: "Interior painters near Orlando Florida delivering modern smooth wall finishes for residential homes",
  },
  {
    id: "exterior-painting",
    title: "Exterior Painting",
    description: "Boost your home's curb appeal with our expert exterior painting services. Built to withstand Florida's weather.",
    features: ["House Exterior Painting", "Stucco Painting", "Pressure Washing", "Weather-Resistant Finishes"],
    image: "/images/exterior-painting-orlando-florida.jpg",
    alt: "Exterior painters in Orlando Florida completing stucco home painting with weather resistant finish",
  },
  {
    id: "popcorn-ceiling",
    title: "Popcorn Ceiling Removal",
    description: "Modernize your home by removing outdated popcorn ceilings. We create smooth, contemporary finishes.",
    features: ["Texture Removal", "Smooth Finish Application", "Ceiling Repairs", "Painting After Removal"],
    image: "/images/popcorn-removal-orlando-florida.jpg",
    alt: "Interior house painters in Orlando Florida removing popcorn ceiling textures for modern smooth finish",
  },
  {
    id: "garage-floor",
    title: "Garage Floor Coatings",
    description: "Protect and beautify your garage floor with durable epoxy and polyaspartic coatings that last for years.",
    features: ["Epoxy Coatings", "Polyaspartic Finishes", "Decorative Flakes", "Anti-Slip Textures"],
    image: "https://images.pexels.com/photos/5691535/pexels-photo-5691535.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
    alt: "Painting services in Casselberry Florida installing professional garage floor epoxy coatings",
  },
  {
    id: "deck-staining",
    title: "Deck Painting & Staining",
    description: "Revitalize your outdoor living space with professional deck painting and staining that protects against the elements.",
    features: ["Deck Staining", "Deck Painting", "Wood Restoration", "Sealant Application"],
    image: "https://images.pexels.com/photos/5691639/pexels-photo-5691639.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop",
    alt: "Exterior painting contractors in Oviedo completing deck staining and wood restoration projects",
  },
];

export const Services = ({ showAll = false }) => {
  const [services, setServices] = useState(fallbackServices);
  const [loading, setLoading] = useState(false);
  const [gridRef, gridVisible] = useScrollReveal();

  useEffect(() => {
    const fetchServices = async () => {
      if (!API) return; // No backend, use fallback data already set
      try {
        const response = await axios.get(`${API}/services`);
        if (response.data.services?.length) {
          setServices(response.data.services);
        }
      } catch (error) {
        // Keep using fallback data
        console.log("Using fallback services data");
      }
    };
    fetchServices();
  }, []);

  const displayedServices = showAll ? services : services.slice(0, 3);

  return (
    <section
      data-testid="services-section"
      className="py-20 sm:py-28 bg-brand-ice relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#000000_1px,transparent_1px),linear-gradient(to_bottom,#000000_1px,transparent_1px)] bg-[size:5rem_5rem]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-14">
          <span className="inline-block text-xs sm:text-sm font-medium uppercase tracking-widest text-brand-orange mb-3">
            What We Do
          </span>
          <h2
            data-testid="services-heading"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight uppercase text-brand-navy"
          >
            Our Services
          </h2>
          <p className="font-body text-sm sm:text-base text-brand-slate mt-3 max-w-xl mx-auto">
            Premium residential painting services from a trusted painting company in Orlando. We serve homeowners across Central Florida with professional interior and exterior painting tailored to protect and beautify your property.
          </p>
        </div>

        {/* Grid */}
        <div
          ref={gridRef}
          className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 reveal-stagger ${gridVisible ? 'visible' : ''}`}
        >
          {displayedServices.map((service) => {
            const Icon = iconMap[service.id] || PaintBucket;
            return (
              <div
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="group bg-white border border-border/60 rounded-xl overflow-hidden hover:border-brand-orange/40 card-lift flex flex-col"
              >
                {/* Image */}
                <div className="relative h-44 sm:h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.alt || service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/50 to-transparent" />
                  <div className="absolute bottom-3 left-3">
                    <div className="w-10 h-10 bg-brand-orange rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 flex flex-col flex-grow">
                  <h3 className="font-heading text-base sm:text-lg font-semibold uppercase text-brand-navy group-hover:text-brand-orange transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="font-body text-brand-slate mt-1.5 text-sm leading-relaxed">
                    {service.description}
                  </p>

                  <ul className="mt-3 space-y-1.5 flex-grow">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-brand-slate">
                        <CheckCircle className="w-3.5 h-3.5 text-brand-orange flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Link to={`/quote?service=${service.id}`} className="mt-4">
                    <Button
                      variant="default"
                      className="bg-[#F1522E] text-white font-heading font-semibold uppercase tracking-wide text-sm h-9 px-5 hover:bg-[#d9432a] transition-colors duration-300"
                    >
                      Get Quote
                      <ArrowRight className="ml-1.5 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* View All */}
        {!showAll && services.length > 3 && (
          <div className="text-center mt-10 sm:mt-12">
            <Link to="/services">
              <Button
                data-testid="view-all-services-btn"
                variant="outline"
                size="lg"
                className="border-2 border-brand-navy text-brand-navy font-heading font-semibold uppercase tracking-wide hover:bg-brand-navy hover:text-white transition-all duration-300"
              >
                View All {services.length} Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
